import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { getPreference, setPreference } from "../data/preferencesClient.js";

export const KPI_BAND_KEY = "kpi_band";
export const KpiBandPresentationContext = createContext(false);
export const KpiBandCollectionContext = createContext(false);
const KpiBandContext = createContext(null);
export const useKpiBand = () => useContext(KpiBandContext);

// A keyed owner prevents even a single render of another account's preference.
export function KpiBandProvider({ userId, children }) {
  return <PreferenceOwner key={userId ?? "anonymous"} userId={userId}>{children}</PreferenceOwner>;
}

function PreferenceOwner({ userId, children }) {
  const [mode, setMode] = useState("FULL");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const revision = useRef(0);
  const modeRef = useRef("FULL");
  const queue = useRef(Promise.resolve());
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    let cancelled = false;
    const initialRevision = revision.current;
    if (userId) {
      getPreference(KPI_BAND_KEY).then((value) => {
        if (cancelled || revision.current !== initialRevision) return;
        if (value !== null && value !== "FULL" && value !== "CONDENSED") {
          throw new Error("Invalid saved KPI band preference.");
        }
        modeRef.current = value ?? "FULL";
        setMode(modeRef.current);
      }).catch((err) => {
        if (!cancelled && revision.current === initialRevision) setError(err.message);
      });
    }
    return () => { cancelled = true; alive.current = false; };
  }, [userId]);

  const save = useCallback((next) => {
    const currentRevision = ++revision.current;
    modeRef.current = next;
    setMode(next);
    setError(null);
    if (!userId) {
      setError("Sign in to save your KPI band preference.");
      return;
    }
    setSaving(true);
    // Serialize writes, not just their responses: a slower old PUT must never
    // become the saved value after a newer choice. Unmounted account queues stop.
    queue.current = queue.current.then(async () => {
      if (!alive.current) return;
      try {
        await setPreference(KPI_BAND_KEY, next);
        if (alive.current && revision.current === currentRevision) setError(null);
      } catch (err) {
        if (alive.current && revision.current === currentRevision) setError(err.message);
      } finally {
        if (alive.current && revision.current === currentRevision) setSaving(false);
      }
    });
  }, [userId]);
  const toggle = useCallback(() => save(modeRef.current === "FULL" ? "CONDENSED" : "FULL"), [save]);
  const retry = useCallback(() => save(modeRef.current), [save]);
  return (
    <KpiBandContext.Provider value={{ mode, toggle, error, saving, retry }}>
      {children}
    </KpiBandContext.Provider>
  );
}
